import { useMemo, useState } from "react";
import { BarChart, Card } from "@tremor/react";
import { Segmented } from "antd";

import { type TeamMemberWithUser } from "@fitness-app/data-models/entities/TeamMember";

import { type TrainerMetricsAggregation } from "../hooks/useChatResponseMetrics";

interface TrainerBreachRateChartProps {
  data: TrainerMetricsAggregation[];
  trainersById: Record<string, TeamMemberWithUser>;
}

type DisplayMode = "percent" | "absolute";

export const TrainerBreachRateChart = ({ data, trainersById }: TrainerBreachRateChartProps) => {
  const [displayMode, setDisplayMode] = useState<DisplayMode>("percent");

  const chartData = useMemo(() => {
    return data
      .filter((item) => item.totalResponses + item.totalBreached > 0)
      .map((item) => {
        const trainer = trainersById[item.trainerId];
        const trainerSurname = trainer?.user?.lastName ?? item.trainerId;

        return {
          trainer: trainerSurname,
          "Wskaźnik (%)": item.breachRate,
          "Liczba przekroczeń": item.totalBreached,
        };
      })
      .sort((a, b) =>
        displayMode === "percent"
          ? b["Wskaźnik (%)"] - a["Wskaźnik (%)"]
          : b["Liczba przekroczeń"] - a["Liczba przekroczeń"],
      );
  }, [data, trainersById, displayMode]);

  const valueFormatter = (value: number) => (displayMode === "percent" ? `${value}%` : `${value}`);

  if (chartData.length === 0) {
    return (
      <Card>
        <h3 className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
          Wskaźnik przekroczeń według trenera
        </h3>
        <p className="text-tremor-content dark:text-dark-tremor-content mt-4">Brak danych do wyświetlenia</p>
      </Card>
    );
  }

  // Dynamic height: 28px per trainer, minimum 200px
  const chartHeight = Math.max(200, chartData.length * 28);

  return (
    <Card>
      <div className="flex items-center justify-between">
        <h3 className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
          Wskaźnik przekroczeń według trenera
        </h3>
        <Segmented
          size="small"
          value={displayMode}
          onChange={(value) => setDisplayMode(value as DisplayMode)}
          options={[
            { label: "Procent", value: "percent" },
            { label: "Liczba", value: "absolute" },
          ]}
        />
      </div>
      <BarChart
        className="mt-4"
        style={{ height: chartHeight }}
        data={chartData}
        index="trainer"
        categories={[displayMode === "percent" ? "Wskaźnik (%)" : "Liczba przekroczeń"]}
        colors={["rose"]}
        valueFormatter={valueFormatter}
        layout="vertical"
        showLegend={false}
        showYAxis={true}
        showGridLines={true}
        yAxisWidth={100}
      />
    </Card>
  );
};
